/**
 * @file LegalSaveButtons.tsx
 * @description Кнопки «Сохранить черновик» и главная «Сохранить и
 * опубликовать» / «Показать снова» / «Сохранить» (phase19.16, SB139, SB143;
 * SC34 — одно главное действие). В шапке на ПК и в нижней полосе каркаса на
 * телефоне (CabinetPageHeader кладёт `action` в полосу), поэтому обе кнопки
 * делят ширину пополам, а на узком экране подписи короче («Опубликовать»):
 * на 360 полная подпись уезжала за край. Имя кнопки для скринридера —
 * всегда полное (`aria-label`).
 *
 * Причина «нельзя опубликовать» связана с главной кнопкой через
 * `aria-describedby` — текст рисует страница (`LEGAL_PUBLISH_REASON_ID`).
 */

import { Button } from "../ui/button";
import type { LegalCardButtons } from "../../lib/legal/legal-card-actions";

/** id строки с причиной «нельзя опубликовать». */
export const LEGAL_PUBLISH_REASON_ID = "legal-publish-reason";

/** Короткие подписи для телефона (реестр: полная → короткая). */
const SHORT_LABEL: Readonly<Record<string, string>> = {
  "Сохранить и опубликовать": "Опубликовать",
  "Сохранить черновик": "Сохранить",
};

/** Подпись: короткая до `sm`, полная с `sm`. */
function Label({ text }: { text: string }) {
  const short = SHORT_LABEL[text];
  if (!short) return <>{text}</>;
  return (
    <>
      <span className="sm:hidden">{short}</span>
      <span className="max-sm:hidden">{text}</span>
    </>
  );
}

interface Props {
  buttons: LegalCardButtons;
  /** Что мешает публикации; null — можно. */
  blockReason: string | null;
  /** Нечего сохранять (нет правок). */
  nothingToSave: boolean;
  /** Ошибки в полях. */
  invalid: boolean;
  busy: "save" | "publish" | null;
  onSave: () => void;
  onPublish: () => void;
}

export function LegalSaveButtons({ buttons, blockReason, nothingToSave, invalid, busy, onSave, onPublish }: Props) {
  const mainIsPublish = buttons.main.run === "publish";
  const mainDisabled = invalid || busy !== null || (mainIsPublish ? blockReason !== null : nothingToSave);
  return (
    <div className="flex gap-2 max-lg:[&>*]:flex-1">
      {buttons.save ? (
        <Button variant="outline" aria-label={buttons.save} onClick={onSave} disabled={invalid || nothingToSave || busy !== null} loading={busy === "save"}>
          <Label text={buttons.save} />
        </Button>
      ) : null}
      <Button
        variant="brand"
        aria-label={buttons.main.label}
        onClick={mainIsPublish ? onPublish : onSave}
        disabled={mainDisabled}
        loading={busy === (mainIsPublish ? "publish" : "save")}
        aria-describedby={mainIsPublish && blockReason ? LEGAL_PUBLISH_REASON_ID : undefined}
      >
        <Label text={buttons.main.label} />
      </Button>
    </div>
  );
}
